import { ApprButton } from '@/common/buttons/ApprButton';
import { ValButton } from '@/common/buttons/ValButton';
import { formatDataTest } from '@/common/dataTest/DataTest';
import { useEntityChange } from '@/common/entityChange/EntityChangeContext';
import { useEntityFromRoute } from '@/common/hooks/route/useEntityFromRoute';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { Box, Tab, Tabs } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavState } from '../../navState/NavStateContext';
import { EntityBanner } from '../entityBanner/EntityBanner';
import { EntityForm } from '../entityForm/EntityForm';
import { EntityProvider } from '../EntityProvider';
import { AsOfDate } from '../entityTitle/AsOfDate';
import { EntityEditButton } from '../entityTitle/EntityEditButton';
import { EntityTitle } from '../entityTitle/EntityTitle';
import { HistToggle } from '../entityTitle/HistToggle';
import { useEntityMgr } from '../entityView/EntityMgrContext';
import { EntityFormMode } from '../enumerations/EntityFormMode';
import { TabProvider } from './TabContext';

export const EntityDetail = ({
  tabSpecs: tabSpecsProp,
  augButtons
}) => {

  const { navState, assign } = useNavState();
  const { hist, listComponent, entityObj, setEntityObj, asOfDate, entityFormMode, entityDef } = useEntityMgr();
  const rsrc = useRsrc();
  const { refreshTrigger, subscrToEntityChange, unsubscrToEntityChange } = useEntityChange();
  useEffect(() => {
    subscrToEntityChange(module, entity, [navState.entityId]);
    return () => {
      unsubscrToEntityChange(module, entity, [navState.entityId]);
    };
  }, [navState.entityId]);

  const module = useModuleFromRoute();
  const entity = useEntityFromRoute();

  const FormComponent = EntityProvider.form(entity) ?? EntityForm;
  const tabSpecs = tabSpecsProp ?? [{ id: 'infoTab', label: 'Info', content: <FormComponent /> }] // Default tabSpec is just the Info tab

  const [selectedTabIndex, setSelectedTabIndex] = useState(navState.selectedTabIndex ?? 0);

  const [entityObjLoaded, setEntityObjLoaded] = useState(false);
  useEffect(() => {
    if (entityFormMode === EntityFormMode.ADD) {
      if (navState.entityObj) {
        setEntityObj(navState.entityObj);
      }

      setEntityObjLoaded(true);
      return;
    };

    const rowsReq = {}

    const exprs = [ExprUtils.whereParams('id', navState.entityId)];

    // As Of Date
    if (asOfDate) {
      exprs.push(ExprUtils.fieldExpr('verStartInstant', 'IS_LESS', asOfDate))
      exprs.push(ExprUtils.fieldExpr('verEndInstant', 'IS_GREATER_OR_EQUAL', asOfDate))
      rowsReq.hist = true;
    }

    rowsReq.expr = ExprUtils.and(...exprs);

    if (navState.partition) {
      rowsReq.partition = navState.partition;
    }

    rsrc.one(module, entity, rowsReq).then(entityObj => {
      entityObj ??= {}; // TODO: What to do if asOfDate is before Entity's creation?
      setEntityObj(entityObj);
      setEntityObjLoaded(true);
    })

  }, [refreshTrigger, asOfDate, entityFormMode])

  useEffect(() => {
    if (entityFormMode === EntityFormMode.EDIT) {
      setSelectedTabIndex(0);
    }
  }, [entityFormMode])

  useEffect(() => {
    if (hist) {
      setSelectedTabIndex(0);
    }
  }, [hist])

  const handleTabChange = (newTabIndex) => {
    setSelectedTabIndex(newTabIndex);
    assign('selectedTabIndex', newTabIndex);
  }

  const infoOnly = useMemo(() => {
    return entityFormMode === EntityFormMode.ADD || entityFormMode === EntityFormMode.EDIT || hist;
  }, [entityFormMode, hist])

  // Don't load until entityObj is populated
  if (!entityObjLoaded) {
    return null;
  }


  /* Define the default buttons, and augment if necessary */
  const buttons = [];
  if (entityFormMode !== EntityFormMode.ADD) buttons.push(<AsOfDate key="asOfDate" />, <HistToggle key="histToggle" />);
  if (entityFormMode !== EntityFormMode.ADD && (!entityDef.validatable || ['DRAFT'].includes(entityObj.status))) buttons.push(<EntityEditButton key="entityEditButton" />);
  if (entityDef.validatable && ['DRAFT', 'ACTIVE', 'ERROR'].includes(entityObj.status)) buttons.push(<ValButton key="valButton" />);
  if (entityDef.approvable && entityObj.status === 'DRAFT' && entity!=='PYMT') buttons.push(<ApprButton key="apprButton" />);
  if (augButtons) augButtons(buttons);

  return (
    <>
      <EntityTitle buttons={buttons} />
      <EntityBanner />

      <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Tabs
          value={selectedTabIndex}
          onChange={(event, newTabIndex) => handleTabChange(newTabIndex)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }} // Adds a thin line under the tabs
        >
          {tabSpecs.map((tabSpec, index) => (
            <Tab data-test-id={formatDataTest('entity-tab', tabSpec.label)}
              id={tabSpec.id + 'Tab'} label={tabSpec.label} key={tabSpec.id} disabled={(infoOnly && index > 0) || tabSpec.disabled} />
          ))}
        </Tabs>
        {tabSpecs.map((tabSpec, index) => (
          <TabPanel
            value={selectedTabIndex}
            index={index}
            id={tabSpec.id + 'Panel'}
            key={tabSpec.id + 'Panel'}
          >
            <TabProvider tabId={tabSpec.id} tabLabel={tabSpec.label}>
              {(hist && tabSpec.label === 'Info') ? listComponent : tabSpec.content}
            </TabProvider>
          </TabPanel>
        ))}
      </Box>
    </>
  );
};

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      style={{
        display: value === index ? 'flex' : 'none',
        flexDirection: 'column',
        flexGrow: 1,
        height: '100%',
        minHeight: 0
      }}
      {...other}
    >
      {value === index && (
        <Box sx={{ paddingTop: 1, flexGrow: 1, height: '100%', overflow: 'auto' }}>
          {children}
        </Box>
      )}
    </div>
  );
};
